import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { featured } from "@/mocks/featured";

export default function HeroCarousel() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const total = featured.length;

  const goTo = useCallback((next: number) => {
    setIndex(((next % total) + total) % total);
  }, [total]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [total]);

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) {
      goTo(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  const current = featured[index];

  return (
    <section
      className="relative h-[440px] w-full overflow-hidden bg-primary-950 md:h-[560px]"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {featured.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title}焦点海报`}
            className="h-full w-full object-top object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/30" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-4 pb-14 md:px-6 md:pb-20">
        <div key={current.id} className="animate-rise max-w-2xl">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-accent-500 px-2 py-1 text-xs font-bold text-background-50">
              热播推荐
            </span>
            <span className="rounded-md bg-primary-800/80 px-2 py-1 text-xs font-medium text-accent-300">
              <i className="ri-star-fill mr-1" />
              {current.rating.toFixed(1)}
            </span>
          </div>
          <h2 className="font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
            {current.title}
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-background-100/85 md:text-base">
            {current.tagline}
          </p>
          <p className="mt-2 text-xs text-background-100/60 md:text-sm">{current.meta}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => navigate(`/detail/${current.id}`)}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-fill text-lg" />
              立即观看
            </button>
            <button
              type="button"
              onClick={() => navigate(`/detail/${current.id}`)}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-100/40 bg-background-50/10 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/20"
            >
              查看详情
            </button>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => goTo(index - 1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-primary-950/50 text-background-50 transition-colors hover:bg-accent-500 md:left-6"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        onClick={() => goTo(index + 1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-primary-950/50 text-background-50 transition-colors hover:bg-accent-500 md:right-6"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {featured.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`切换到第${i + 1}张`}
            className={`h-2 cursor-pointer rounded-full transition-all duration-300 ${
              i === index ? "w-6 bg-accent-500" : "w-2 bg-background-100/50 hover:bg-background-100/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}